import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AppSettingsService, GigyaService, TOKEN_STORE_SERVICE, TokenService, TokenStoreService } from '../services';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	isRefreshingToken: boolean;

	constructor(
		private gigyaService: GigyaService,
		private tokenService: TokenService,
		private appSettingsService: AppSettingsService,
		@Optional() @Inject(TOKEN_STORE_SERVICE) private tokenStoreService?: TokenStoreService
	) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (this.appSettingsService.settings.tokenAuthExclusions?.some((api: any) => req.url.includes(api))) {
			return next.handle(req);
		}

		let requestHandler: Observable<HttpEvent<any>>;
		if (this.tokenStoreService) {
			requestHandler = this.tokenStoreService
				.selectToken()
				.pipe(switchMap((token) => next.handle(this.addToken(req, token))));
		} else {
			requestHandler = next.handle(this.addToken(req, this.gigyaService.getToken()));
		}

		return requestHandler.pipe(
			catchError((err) => {
				if (err instanceof HttpErrorResponse && err.status === 401) {
					// token is expired refresh and try again
					return this.handleUnauthorized(req, next);
				} else {
					return throwError(err);
				}
			})
		);
	}

	addToken(req: HttpRequest<any>, token: string | null | undefined): HttpRequest<any> {
		return req.clone({ headers: req.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) });
	}

	handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
		if (this.isRefreshingToken) {
			// if a previously intercepted call has triggered token update then wait for the token to be updated before continuing
			return this.tokenSubject.pipe(
				filter((token: string | null) => token != null),
				take(1),
				switchMap((token) => next.handle(this.addToken(req, token)))
			);
		}

		this.isRefreshingToken = true;
		// Reset here so that the following requests wait until the token
		// comes back from the refreshToken call.
		this.tokenSubject.next(null);

		let refreshMethod: Observable<string>;

		if (this.gigyaService.state?.preAuth) {
			// get a new token via affinity API
			refreshMethod = this.tokenService.refreshToken();
		} else {
			// get a new token via gigyaService.refreshToken
			refreshMethod = this.gigyaService.refreshToken();
		}

		return refreshMethod.pipe(
			switchMap((newToken: string) => {
				// did we get a new token retry previous request
				if (newToken) {
					this.tokenSubject.next(newToken);
					this.tokenStoreService?.storeToken(newToken);
					return next.handle(this.addToken(req, newToken));
				}

				// If we don't get a new token, logout.
				this.gigyaService.handleLogout();
				return throwError('');
			}),
			catchError((error) => {
				// If there is an exception calling 'refreshToken', logout.
				this.gigyaService.handleLogout();
				return throwError(error);
			}),
			finalize(() => {
				this.isRefreshingToken = false;
			})
		);
	}
}
